import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Mail, Send, RefreshCw } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/button';

const LIST_MESSAGES = gql`
  query ListMessages {
    depListUserMessages {
      messages {
        id
        senderString
        recipientString
        subject
        createdAt
      }
      totalCount
    }
  }
`;

const MIGRATE_EMAILS = gql`
  mutation MigrateEmails($ids: [Int!]!) {
    migrateEmails(ids: $ids) {
      message
      
    }
  }
`;

// Decode RFC2047 "encoded-word" syntax (handles Q and B encodings)
function decodeRFC2047(str: string): string {
  return str.replace(/=\?([^?]+)\?([bqBQ])\?([^?]+)\?=/g, (_match, _charset, encoding, text) => {
    const enc = encoding.toUpperCase();
    if (enc === 'Q') {
      // Quoted-printable: _ → space, =XX → byte
      return text
        .replace(/_/g, ' ')
        .replace(/=([A-F0-9]{2})/g, (_m: string, hex: string) =>
          String.fromCharCode(parseInt(hex, 16))
        );
    }
    if (enc === 'B') {
      // Base64
      try {
        return atob(text);
      } catch {
        return text;
      }
    }
    return text;
  });
}

export const ListAllMessages: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data, loading, error, refetch } = useQuery(LIST_MESSAGES, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all', // Continue even if there are GraphQL errors
  });

  // typed messages array - check if response has messages array or is direct array
  const allMessages = (data?.depListUserMessages?.messages || data?.depListUserMessages) as
    | { id: string; senderString: string; recipientString: string; subject: string; createdAt: string }[]
    | undefined;
  
  // Filter messages based on search term
  const filteredMessages = allMessages?.filter(msg => 
    decodeRFC2047(msg.subject).toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Apply client-side pagination to filtered results
  const messages = filteredMessages.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
  const totalCount = filteredMessages.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const [migrateEmails, { data: migrateData, loading: migrateLoading, error: migrateError }] =
    useMutation(MIGRATE_EMAILS);

  // early returns for load/error
  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm">Loading messages…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-500">Error: {error.message}</p>
      </div>
    );
  }

  const toggleSelect = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
      return;
    }
    setSelectedIds([...selectedIds, id]);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
    setSelectedIds([]); // Clear selections when changing pages
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(0); // Reset to first page when changing page size
    setSelectedIds([]); // Clear selections when changing page size
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPageIndex(0); // Reset to first page when searching
    setSelectedIds([]); // Clear selections when searching
  };

  return (
    <div className="flex flex-col h-screen w-full p-6">
      {/* Header */}
      <header className="flex-none">
        <h1 className="text-2xl font-semibold">Migrate Emails</h1>
        <div className="flex items-center justify-between mt-4 mb-2">
          <div className="text-sm text-gray-600">
            <div className="flex items-center mt-3 mb-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by subject..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            Showing {totalCount > 0 ? pageIndex * pageSize + 1 : 0} to {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount} 
            {searchTerm ? ' filtered' : ''} messages
            {searchTerm && allMessages && (
              <span className="text-gray-500 ml-1">(from {allMessages.length} total)</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm">
              Show
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                disabled={isPaginationLoading}
                className="mx-1 text-sm border rounded px-2 py-1 disabled:opacity-50"
              >
                {[10, 20, 50, 100].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              per page
              {isPaginationLoading && <span className="ml-2 text-xs text-blue-600">Loading...</span>}
            </label>
          </div>
        </div>
      </header>

      {/* Scrollable Message List */}
      <section className="flex-1 overflow-auto border rounded-md min-h-0 max-h-[calc(100vh-400px)] bg-white">
        <ul className="divide-y">
          {messages?.map(msg => (
            <li key={msg.id} className="flex items-start p-3 hover:bg-gray-50 transition-colors">
              <div className="pt-1">
                <Checkbox
                  checked={selectedIds.includes(msg.id)}
                  onCheckedChange={() => toggleSelect(msg.id)}
                />
              </div>
              <div className="ml-3 flex-1">
                <div className="font-medium text-sm mb-1">
                  {decodeRFC2047(msg.subject)}
                </div>
                <div className="text-xs text-gray-600">
                  From: {msg.senderString} | To: {msg.recipientString} |{' '}
                  {new Date(msg.createdAt).toLocaleDateString()}
                </div>
              </div>
            </li>
          ))}
        </ul>
        {messages.length === 0 && !loading && (
          <EmptyState
            icon={Mail}
            title={searchTerm ? "No matching messages" : "No messages available"}
            description={searchTerm ? `We couldn't find any messages matching "${searchTerm}"` : "The message list is currently empty."}
            action={searchTerm ? (
              <Button variant="outline" size="sm" onClick={() => handleSearchChange('')}>
                Clear Search
              </Button>
            ) : undefined}
            className="py-20"
          />
        )}
      </section>

      {/* Pagination Controls */}
      <div className="flex-none flex items-center justify-between py-4 border-t">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(0)}
            disabled={pageIndex === 0 || isPaginationLoading}
          >
            First
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pageIndex - 1)}
            disabled={pageIndex === 0 || isPaginationLoading}
          >
            Previous
          </Button>
          <span className="text-sm px-4">
            Page <strong>{pageIndex + 1}</strong> of <strong>{totalPages}</strong>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={pageIndex >= totalPages - 1 || isPaginationLoading}
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(totalPages - 1)}
            disabled={pageIndex >= totalPages - 1 || isPaginationLoading}
          >
            Last
          </Button>
        </div>
      </div>

      {/* Footer with action */}
      <footer className="flex-none py-4 border-t mt-auto">
        <div className="flex items-center justify-end">
          {migrateError && (
            <p className="text-sm text-red-500 mr-4 italic">Error: {migrateError.message}</p>
          )}
          <Button
            onClick={() => migrateEmails({ variables: { ids: selectedIds } })}
            disabled={migrateLoading || selectedIds.length === 0}
            className="flex items-center gap-2 min-w-[180px] shadow-sm"
          >
            {migrateLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {migrateLoading ? 'Migrating...' : `Migrate ${selectedIds.length} Selected`}
          </Button>
        </div>
        {migrateData?.migrateEmails && (
          <p className="text-sm text-green-600 mt-2 text-right">
            ✔ {migrateData.migrateEmails.message}
          </p>
        )}
      </footer>
    </div>
  );
};
